import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import {
  DASHBOARD_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";
import {
  markMaterialRead,
  saveMaterial,
  unsaveMaterial,
  updateMaterialProgress,
} from "../../services/materialsService";
import Spinner from "../../components/Spinner/Spinner";
import PdfReader from "./PdfReader";
import YouTubePlayer from "./YouTubePlayer";
import {
  ResourceTypeBadge,
  directVideoSrc,
  imageSrc,
  materialHref,
  officeEmbedSrc,
  pdfFrameSrc,
  prettyHost,
  youTubeId,
} from "./MaterialPreview";

// Minimum gap between THROTTLED progress writes (only the continuous ~5s
// playback poll goes through the throttle). Kept comfortably below the 5s poll
// interval so timer jitter can't drop a poll on the strict `< THROTTLE` check.
// Discrete events (page turn, pause, unmount) bypass the throttle via saveNow.
const PROGRESS_THROTTLE_MS = 2500;

// A throttled, fire-and-forget progress saver bound to one material. Returns
// `save(body)` (throttled, drops errors) and `saveNow(body)` (immediate — used
// on unmount/pause where the trailing throttle window would be lost). Both
// no-op once the component unmounts via the shared `alive` ref.
function useProgressSaver(uuid) {
  const lastSentRef = useRef(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const saveNow = useCallback(
    (body) => {
      if (!uuid || !body) return;
      lastSentRef.current = Date.now();
      // Intentionally NOT gated on aliveRef: child players/readers flush in
      // their own cleanup, which runs BEFORE this hook's cleanup, so the
      // terminal write must still land. Fire-and-forget — resume tracking must
      // never surface an error to the teacher, and no AbortController so the
      // final unmount write completes even as the page tears down.
      updateMaterialProgress({ uuid, body }).catch(() => {});
    },
    [uuid]
  );

  const save = useCallback(
    (body) => {
      if (!aliveRef.current) return;
      const now = Date.now();
      if (now - lastSentRef.current < PROGRESS_THROTTLE_MS) return;
      saveNow(body);
    },
    [saveNow]
  );

  return { save, saveNow };
}

// Back link to the teacher library, reused by the loaded + error states.
function BackLink() {
  const { t } = useTranslation();
  return (
    <Link
      to="/my-materials"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M15 19l-7-7 7-7" />
      </svg>
      {t("materials.detailBack")}
    </Link>
  );
}

// Prominent "open original" external button, shown once in the header so the
// reliable path is always one click away even when a site blocks framing.
function OpenOriginalButton({ url }) {
  const { t } = useTranslation();
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition"
    >
      {t("materials.openOriginal")}
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

// Native <video> player that resumes from `startSeconds` and reports the
// playback position up: `onProgress(seconds)` (throttled by the caller) on the
// ~4x/s timeupdate stream, and `onFlush(seconds)` (immediate) on pause and
// unmount so the terminal position can't be swallowed by the throttle window.
function DirectVideoPlayer({ src, title, startSeconds, onProgress, onFlush }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;
  const startRef = useRef(startSeconds);
  startRef.current = startSeconds;

  const currentSeconds = () => Math.floor(videoRef.current?.currentTime || 0);

  const report = () => {
    if (videoRef.current) onProgressRef.current?.(currentSeconds());
  };
  const flush = useCallback(() => {
    if (videoRef.current) onFlushRef.current?.(currentSeconds());
  }, []);

  // Persist the position on unmount AND when the tab is backgrounded/closed
  // (effect cleanup doesn't run on pagehide).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [flush]);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video && startRef.current > 0) video.currentTime = startRef.current;
  };

  return (
    <video
      ref={videoRef}
      controls
      src={src}
      onLoadedMetadata={handleLoadedMetadata}
      onTimeUpdate={report}
      onPause={flush}
      className="w-full max-h-[80vh] rounded-2xl border border-slate-200 bg-slate-900 shadow-sm"
    >
      {t("materials.viewerVideoUnsupported")}
    </video>
  );
}

// Type-specific viewer. Resolves the playable/readable source from
// file_url || url and renders a reader (pdf), player (video), or framed page
// (article/link) per the material's resource_type. `onPageProgress` /
// `onSecondsProgress` carry the resume writes up to MaterialDetail; the resume
// point is read from `material.my_state`.
function MaterialViewer({
  material,
  onPageProgress,
  onSecondsProgress,
  onSecondsFlush,
}) {
  const { t } = useTranslation();
  const src = materialHref(material);
  const type = material.resource_type;
  const state = material.my_state || {};

  if (!src) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 py-16 text-center">
        <p className="text-sm font-semibold text-slate-500">
          {t("materials.viewerNoSource")}
        </p>
      </div>
    );
  }

  if (type === "pdf") {
    // UPLOADED PDF (same-origin file_url): paged react-pdf reader with resume.
    // pdf.js can't fetch cross-origin/CORS-blocked external links, so an
    // EXTERNAL PDF (url only) keeps the unchanged gview-iframe viewer below.
    if (material.file_url) {
      const externalFallback = (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 max-w-md leading-relaxed">
            {t("materials.pdfError")}
          </p>
          <iframe
            src={pdfFrameSrc(material)}
            title={t("materials.viewerPdf", { title: material.title })}
            className="w-full h-[80vh] rounded-2xl border border-slate-200 bg-white shadow-sm"
          />
        </div>
      );
      return (
        <PdfReader
          file={material.file_url}
          title={t("materials.viewerPdf", { title: material.title })}
          initialPage={state.progress_page || 1}
          onPageChange={onPageProgress}
          fallback={externalFallback}
        />
      );
    }
    return (
      <div className="space-y-3">
        <p className="text-xs text-slate-500 max-w-md leading-relaxed">
          {t("materials.embedNote")}
        </p>
        <iframe
          src={pdfFrameSrc(material)}
          title={t("materials.viewerPdf", { title: material.title })}
          className="w-full h-[80vh] rounded-2xl border border-slate-200 bg-white shadow-sm"
        />
      </div>
    );
  }

  if (type === "video") {
    // YouTube: IFrame Player API (videoId) so we can seekTo/resume + poll.
    const videoId = youTubeId(src);
    if (videoId) {
      return (
        <YouTubePlayer
          videoId={videoId}
          startSeconds={state.progress_seconds || 0}
          onProgress={onSecondsProgress}
          onFlush={onSecondsFlush}
          title={t("materials.viewerVideo", { title: material.title })}
        />
      );
    }
    // Direct video file: native <video> with currentTime resume.
    if (directVideoSrc(src)) {
      return (
        <DirectVideoPlayer
          src={src}
          title={t("materials.viewerVideo", { title: material.title })}
          startSeconds={state.progress_seconds || 0}
          onProgress={onSecondsProgress}
          onFlush={onSecondsFlush}
        />
      );
    }
    // Unknown video host: frame it (no tracking); the header's Open-original
    // covers the case where the host blocks framing.
    return (
      <div className="aspect-video w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-sm">
        <iframe
          src={src}
          title={t("materials.viewerVideo", { title: material.title })}
          className="w-full h-full"
          allowFullScreen
        />
      </div>
    );
  }

  // other — an arbitrary uploaded file. Images preview inline directly; Office
  // docs (Word/PPT/Excel) preview via Microsoft's Office Online viewer (needs a
  // publicly reachable URL — blank on localhost); anything else is a download
  // prompt. None of these resume (PDF-only).
  if (type === "other") {
    const image = imageSrc(src);
    if (image) {
      return (
        <div className="flex justify-center rounded-2xl border border-slate-200 bg-slate-100 p-3 overflow-auto max-h-[80vh]">
          <img
            src={image}
            alt={material.title}
            className="max-w-full h-auto rounded-lg shadow-md"
          />
        </div>
      );
    }
    const office = officeEmbedSrc(src);
    if (office) {
      return (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 max-w-md leading-relaxed">
            {t("materials.officeEmbedNote")}
          </p>
          <iframe
            src={office}
            title={t("materials.viewerPage", { title: material.title })}
            className="w-full h-[80vh] rounded-2xl border border-slate-200 bg-white shadow-sm"
          />
        </div>
      );
    }
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 py-16 px-6 flex flex-col items-center text-center gap-4">
        <span className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
          </svg>
        </span>
        <div>
          <p className="text-sm font-bold text-slate-700">{material.title}</p>
          <p className="mt-1 text-xs text-slate-400 max-w-xs leading-relaxed">
            {t("materials.otherViewerNote")}
          </p>
        </div>
      </div>
    );
  }

  // article / link — many sites block framing (X-Frame-Options); the header's
  // Open-original is the reliable path if the embed stays blank.
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 max-w-md leading-relaxed">
        {t("materials.embedNote")}
      </p>
      <iframe
        src={src}
        title={t("materials.viewerPage", { title: material.title })}
        className="w-full h-[80vh] rounded-2xl border border-slate-200 bg-white shadow-sm"
      />
    </div>
  );
}

export default function MaterialDetail() {
  const { t } = useTranslation();
  const { uuid } = useParams();
  const { user } = useContext(UserContext);

  const canView = hasPermission(user, DASHBOARD_PERMISSION_CODES.TEACHER_VIEW);

  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Optimistic save state, mirrored from the loaded material's my_state.
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // markMaterialRead resolves the material through the teacher-visible scope and
  // returns the full record (incl my_state) while marking it read, so it doubles
  // as the loader. A 404 means it isn't visible to this teacher.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    markMaterialRead({ uuid, signal: controller.signal })
      .then((data) => {
        setMaterial(data);
        setSaved(!!data?.my_state?.saved);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        // 404 = genuinely not visible to this teacher; anything else (network,
        // 5xx) is transient and offers a retry rather than "doesn't exist".
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [uuid, reloadKey]);

  // Throttled, fire-and-forget resume writes. `saveNow` is used for terminal
  // events (page turn, pause, unmount) where waiting on the throttle window
  // would drop the last position; `save` throttles the continuous stream
  // (~5s poll / timeupdate). Page changes are discrete and meaningful, so they
  // go through saveNow; the continuous seconds stream goes through save.
  const { save: saveProgress, saveNow: saveProgressNow } = useProgressSaver(uuid);
  const onPageProgress = useCallback(
    (page) => saveProgressNow({ page }),
    [saveProgressNow]
  );
  const onSecondsProgress = useCallback(
    (seconds) => saveProgress({ seconds }),
    [saveProgress]
  );
  // Terminal video events (pause/end/unmount) bypass the throttle so the final
  // position always lands.
  const onSecondsFlush = useCallback(
    (seconds) => saveProgressNow({ seconds }),
    [saveProgressNow]
  );

  const toggleSave = async () => {
    if (saving || !material) return;
    const next = !saved;
    setSaved(next); // optimistic
    setSaving(true);
    try {
      if (next) await saveMaterial({ uuid: material.uuid });
      else await unsaveMaterial({ uuid: material.uuid });
    } catch {
      setSaved(!next); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  // Deep-link guard for users who reach the detail page without the permission.
  if (!canView) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="p-6 bg-slate-50 min-h-full">
        <div className="max-w-5xl mx-auto">
          <BackLink />
          <div className="mt-10 flex justify-center">
            <Spinner label={t("materials.loading")} />
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 bg-slate-50 min-h-full">
        <div className="max-w-5xl mx-auto">
          <BackLink />
          <div className="mt-6 flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50">
            <span className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <p className="text-sm font-bold text-slate-700">{t("materials.loadErrorTitle")}</p>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
            >
              {t("common.retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !material) {
    return (
      <div className="p-6 bg-slate-50 min-h-full">
        <div className="max-w-5xl mx-auto">
          <BackLink />
          <div className="mt-6 flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50">
            <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <p className="text-sm font-bold text-slate-700">
              {t("materials.notFoundTitle")}
            </p>
            <p className="mt-1 text-xs text-slate-400 max-w-xs">
              {t("materials.notFoundHint")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const original = materialHref(material);
  const host = prettyHost(original);

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-5xl mx-auto">
        <BackLink />

        <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ResourceTypeBadge type={material.resource_type} />
                {host && (
                  <span className="text-[11px] font-semibold text-slate-500 truncate">
                    {host}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-slate-900 leading-snug">
                {material.title}
              </h2>
              {material.description && (
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {material.description}
                </p>
              )}
              {(material.subjects || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {material.subjects.map((s) => (
                    <span
                      key={s.uuid}
                      className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-bold"
                    >
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={toggleSave}
                disabled={saving}
                aria-pressed={saved}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition disabled:opacity-50 ${
                  saved
                    ? "text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
                    : "text-slate-600 border-slate-200 bg-white hover:text-indigo-600 hover:border-indigo-200"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill={saved ? "currentColor" : "none"}
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z" />
                </svg>
                {saved ? t("materials.saved") : t("materials.save")}
              </button>
              <OpenOriginalButton url={original} />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <MaterialViewer
            material={material}
            onPageProgress={onPageProgress}
            onSecondsProgress={onSecondsProgress}
            onSecondsFlush={onSecondsFlush}
          />
        </div>
      </div>
    </div>
  );
}
